import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Modal, message } from "antd";
import { incidentService } from "@iso/services";

const useIncidentActions = ({ onRefresh }) => {
  const history = useHistory();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [approvalModalIncident, setApprovalModalIncident] = useState(null);
  const [classifyModalVisible, setClassifyModalVisible] = useState(false);
  const [classifyIncident, setClassifyIncident] = useState(null);
  const [investigationVisible, setInvestigationVisible] = useState(false);
  const [investigationIncident, setInvestigationIncident] = useState(null);
  const [deleteIncident, setDeleteIncident] = useState(null);

  const handleEditClick = (incident) => {
    setSelectedIncident(incident);
    setModalVisible(true);
  };

  const handleHistoryClick = (incident) => {
    setSelectedIncident(incident);
    setHistoryModalVisible(true);
  };

  const handleActivityClick = (incident) => {
    setSelectedIncident(incident);
    setActivityModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedIncident(null);
  };

  const handleHistoryModalClose = () => {
    setHistoryModalVisible(false);
    setSelectedIncident(null);
  };

  const handleActivityModalClose = () => {
    setActivityModalVisible(false);
    setSelectedIncident(null);
  };

  const handleClassifyClick = (incident) => {
    setClassifyIncident(incident);
    setClassifyModalVisible(true);
  };

  const handleClassifyNavigate = (incident) => {
    history.push(`/incidents/${incident.id}/investigation-selection`);
  };

  const handleChangeInvestigationType = (incident) => {
    history.push(`/incidents/${incident.id}/investigation-selection?mode=reclassify`);
  };

  const handleInvestigationClick = (incident) => {
    if (incident.investigation_type === "five_why") {
      history.push(`/incidents/${incident.id}/investigation/five-why`);
    } else if (incident.investigation_type === "icam") {
      history.push(`/incidents/${incident.id}/investigation/icam`);
    } else if (incident.investigation_type === "factual") {
      history.push(`/incidents/${incident.id}/investigation/factual`);
    } else {
      // Fallback to drawer panel for other types
      setInvestigationIncident(incident);
      setInvestigationVisible(true);
    }
  };

  const handleCorrectiveActionsClick = (incident) => {
    history.push(`/incidents/${incident.id}/corrective-actions`);
  };

  const handleLessonsLearnedClick = (incident) => {
    history.push(`/incidents/${incident.id}/lessons-learned`);
  };

  const handleClassifyClose = () => {
    setClassifyModalVisible(false);
    setClassifyIncident(null);
  };

  const handleInvestigationClose = () => {
    setInvestigationVisible(false);
    setInvestigationIncident(null);
  };

  /**
   * Fire one configured escalation rule for an incident.
   *
   * The endpoint answers 200 even when nothing was delivered, reporting the
   * outcome in `result.status`, so a send that reached nobody must be surfaced
   * as an error rather than assumed successful.
   *
   * @param {object} incident
   * @param {{ code: string, label: string }} rule
   */
  const handleNotify = (incident, rule) => {
    Modal.confirm({
      title: rule.label,
      content: `Send "${rule.label}" for incident ${incident.report_number}?`,
      okButtonProps: { style: { backgroundColor: '#0B2A1B', borderColor: '#0B2A1B' } },
      onOk: async () => {
        try {
          const response = await incidentService.notify(incident.id, rule.code);
          const result = response?.result;

          if (result?.status === 'failed') {
            message.error(result.message || `${rule.label} could not be delivered.`);
          } else if (result?.status === 'partial') {
            message.warning(result.message || `${rule.label} only reached some recipients.`);
          } else {
            message.success(result?.message || `${rule.label} sent.`);
          }

          onRefresh();
        } catch (err) {
          message.error(err?.response?.data?.message || `Failed to send ${rule.label}.`);
        }
      },
    });
  };

  const handleCloseIncident = (incident) => {
    Modal.confirm({
      title: 'Close Incident',
      content: `Are you sure you want to close incident ${incident.report_number}? This action requires all preconditions to be met.`,
      okButtonProps: { style: { backgroundColor: '#0B2A1B', borderColor: '#0B2A1B' } },
      onOk: async () => {
        try {
          await incidentService.closeIncident(incident.id);
          message.success('Incident closed successfully.');
          onRefresh();
        } catch (err) {
          message.error(err?.response?.data?.message || 'Failed to close incident.');
        }
      },
    });
  };

  const handleDeleteClick = (incident) => {
    setDeleteIncident(incident);
  };

  const handleDeleteClose = () => {
    setDeleteIncident(null);
  };

  const handleDeleted = () => {
    setDeleteIncident(null);
    onRefresh();
  };

  const handleIncidentModalSaved = () => {
    onRefresh();
  };

  const handleApprovalOpen = (incident) => {
    setApprovalModalIncident(incident);
  };

  const handleApprovalClose = () => {
    setApprovalModalIncident(null);
  };

  const openCreateModal = () => {
    setSelectedIncident(null);
    setModalVisible(true);
  };

  return {
    // State
    modalVisible,
    selectedIncident,
    historyModalVisible,
    activityModalVisible,
    approvalModalIncident,
    classifyModalVisible,
    classifyIncident,
    investigationVisible,
    investigationIncident,
    deleteIncident,

    // Handlers
    handleEditClick,
    handleHistoryClick,
    handleActivityClick,
    handleModalClose,
    handleHistoryModalClose,
    handleActivityModalClose,
    handleClassifyClick,
    handleClassifyNavigate,
    handleChangeInvestigationType,
    handleInvestigationClick,
    handleCorrectiveActionsClick,
    handleLessonsLearnedClick,
    handleClassifyClose,
    handleInvestigationClose,
    handleNotify,
    handleCloseIncident,
    handleDeleteClick,
    handleDeleteClose,
    handleDeleted,
    handleIncidentModalSaved,
    handleApprovalOpen,
    handleApprovalClose,
    openCreateModal,
  };
};

export default useIncidentActions;
